import moment from 'moment';
import { MAIN_DATA } from '@mos-front-components/public/es/components/MainData';
import { createSelector } from 'reselect';

import { genderFromServer } from '../../helpers/gender';
import validator from '../../helpers/validator';
import {
  CITIZENSHIP_TYPES,
  CITIZENSHIP_VALUES,
  MAIN_DATA_FIELD_NAMES
} from '../../constants/citizenship';
import { applicantTypeSelector } from '../applicant/selectors';
import { APPLICANT_TYPES } from '../../constants/applicant';
import { API_DATE_FORMAT, DATE_FORMAT } from '../../helpers/date';

export const mainDataSelector = state => state.mainData;

export const mainDataValuesSelector = createSelector(
  mainDataSelector,
  subState => subState.data
);

export const mainDataBlurSelector = createSelector(
  mainDataSelector,
  subState => subState.blur
);

export const mainDataFullValuesSelector = createSelector(
  mainDataSelector,
  subState => ({
    ...subState.data,
    snils: subState.snils ? subState.snils : {}
  })
);

export const getUserBirthdateSelector = createSelector(
  mainDataValuesSelector,
  subState => moment(subState.birthdate, DATE_FORMAT).clone()
);

export const getUnderFourteenSelector = createSelector(
  getUserBirthdateSelector,
  birthdate =>
    Math.abs(moment(birthdate, DATE_FORMAT).diff(moment(), 'years')) < 14
);

export const getBirthdateBeforeFourteenAndThirtyOneDaysAgo = createSelector(
  getUserBirthdateSelector,
  birthdate => {
    const fourteenYearsAgo = moment()
      .subtract(31, 'days')
      .subtract(14, 'years');
    const dateOfBirth = moment(birthdate, DATE_FORMAT);
    return dateOfBirth.isBefore(fourteenYearsAgo);
  }
);

export const getBirthdateBeforeFourteenAndHalfAgo = createSelector(
  getUserBirthdateSelector,
  birthdate => {
    const fourteenAndHalfYearsAgo = moment()
      .subtract(180, 'days')
      .subtract(14, 'years');
    const dateOfBirth = moment(birthdate, DATE_FORMAT);

    return dateOfBirth.isAfter(fourteenAndHalfYearsAgo);
  }
);

export const getForeignUnderSixteen = createSelector(
  getUserBirthdateSelector,
  mainDataValuesSelector,
  (birthdate, subState) => {
    return subState.citizenship === CITIZENSHIP_VALUES[2]
      ? Math.abs(moment(birthdate, DATE_FORMAT).diff(moment(), 'years')) <= 16
      : null;
  }
);

export const mainDataValidationSelector = createSelector(
  applicantTypeSelector,
  mainDataValuesSelector,
  (applicantType, mainData) => validator(mainData, MAIN_DATA)
);

export const mainDataFullValidationSelector = createSelector(
  applicantTypeSelector,
  mainDataValuesSelector,
  getBirthdateBeforeFourteenAndHalfAgo,
  (applicantType, mainData, beforeFourteenAndHalf) => {
    return validator(
      mainData,
      MAIN_DATA,
      null,
      beforeFourteenAndHalf && applicantType === APPLICANT_TYPES.participant
    );
  }
);

export const mainDataErrorsSelector = createSelector(
  mainDataValidationSelector,
  subState => subState.errors
);

export const mainDataDisabledSelector = createSelector(
  mainDataValidationSelector,
  subState => subState.disabled
);

export const mainDataIsValidSelector = createSelector(
  mainDataValidationSelector,
  subState => subState.valid
);

export const mainDataEditableFieldsSelector = createSelector(
  mainDataSelector,
  subState => subState.editableFields
);

export const mainDataChildrenSelector = createSelector(
  mainDataValuesSelector,
  subState =>
    Array.isArray(subState.children)
      ? subState.children.filter(child => child.is_birth_certificate_verified)
      : []
);

const getSnils = snils => {
  if (!snils) {
    return '';
  }

  if (typeof snils === 'string') {
    return snils;
  }

  return snils.snils_number || '';
};

export const getUserSnilsSelector = createSelector(
  mainDataFullValuesSelector,
  subState => getSnils(subState.snils)
);

export const mainDataBirthRegPlaceSelector = createSelector(
  mainDataValuesSelector,
  subState => subState[MAIN_DATA_FIELD_NAMES.birthRegistrationPlace]
);

export const appealSubmitPersonSelector = createSelector(
  mainDataFullValuesSelector,
  subState => ({
    middle_name_absent: !subState.middleName,
    snils: getSnils(subState.snils),
    last_name: subState.lastName || '',
    first_name: subState.firstName || '',
    middle_name: subState.middleName || '',
    gender: genderFromServer(subState.gender),
    birth_day: subState.birthdate
      ? moment(subState.birthdate, DATE_FORMAT)
          .clone()
          .format(API_DATE_FORMAT)
      : '',
    phone: `${subState.phone}` || '',
    email: subState.email || '',
    citizenship: CITIZENSHIP_TYPES[subState.citizenship] || ''
  })
);
